import { AloftDate, RecommendedCompsSources, isDefined } from '@aloft/toolkit-shared';
import {
  Block,
  Flex,
  Icon,
  ProductColor,
  SmallLoader,
  Tooltip,
  Typography,
  theme,
} from '@aloft/toolkit-sui';
import {
  SortingState,
  Table,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';

import {
  LocationType,
  formatProximityRequirementTooltip,
} from '../../../utils/locationTypeProximityRequirementMiles.ts';
import {
  formatSaleDateRequirementTooltip,
  isSaleDateOutOfBounds,
} from '../../../utils/locationTypeSaleDateRequirementMonths.ts';
import { addressColumn } from '../internal/gridHelpers/addressColumn.tsx';
import { getSortedRowModelWithPinnedSubject } from '../internal/gridHelpers/getSortedRowModelWithPinnedSubject.ts';
import { rentalStatusColumn } from '../internal/gridHelpers/rentalStatusColumn.tsx';
import { makeSelectionColumn } from '../internal/gridHelpers/selectionColumn.tsx';
import { statusColumn } from '../internal/gridHelpers/statusColumn.tsx';
import { CompSelectionProperty, CompSelectionState } from '../types.ts';

const INITIAL_PAGE_INDEX = 0;
/*
We probably don't need to worry about paging. We should just be able to load
everything we get back, which should be less than this, and users can scroll
through.
*/
const PAGE_SIZE = 500;

function formatDateCell(date?: { format: (fmt: string) => string }) {
  return date ? date.format('MM/yy') : '';
}

function booleanToYesNo(value?: boolean) {
  if (value === undefined) return '';
  return value ? 'Yes' : 'No';
}

function formatAddressCell(address: CompSelectionProperty['address']) {
  return `${address?.AddressLineText}, ${address?.CityName}, ${address?.StateCode} ${address?.PostalCode}`;
}

function UADRatingCell({
  rating,
  prefix,
  isLoading,
  hasFailed,
  isSkipped,
  failureTooltip,
  commentary,
}: {
  rating?: number;
  prefix: 'Q' | 'C';
  isLoading?: boolean;
  hasFailed?: boolean;
  isSkipped?: boolean;
  failureTooltip: string;
  commentary?: string;
}) {
  if (isLoading) {
    return (
      <Flex alignItems="center" width="100%">
        <SmallLoader size={14} />
      </Flex>
    );
  }

  if (rating !== undefined) {
    const label = `${prefix}${rating}`;
    return commentary ? (
      <Tooltip label={commentary}>
        <Typography __dangerDoNotUseSx={{ cursor: 'help' }}>{label}</Typography>
      </Tooltip>
    ) : (
      label
    );
  }

  if (isSkipped) {
    return (
      <Tooltip label="UAD ratings image analysis is only available for the first 20 recommended comps.">
        <Typography variant="body2" color="textSecondary" __dangerDoNotUseSx={{ cursor: 'help' }}>
          -
        </Typography>
      </Tooltip>
    );
  }

  if (hasFailed) {
    return (
      <Tooltip label={failureTooltip}>
        <Typography variant="body2" color="textSecondary">
          ?
        </Typography>
      </Tooltip>
    );
  }

  return '';
}

function UADRatingHeader({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <Flex alignItems="center" gap={1}>
      <Tooltip label={tooltip}>
        <Block __dangerDoNotUseSx={{ cursor: 'help' }}>
          <Icon icon="sparkle" size="sm" color="buttonDefault" />
        </Block>
      </Tooltip>
      {label}
    </Flex>
  );
}

export const useRecommendedCompSelectionTable = (
  data: CompSelectionProperty[],
  subject: CompSelectionProperty,
  comps: CompSelectionProperty[],
  sorting: SortingState,
  setSorting: Dispatch<SetStateAction<SortingState>>,
  rowSelection: CompSelectionState,
  setRowSelection: Dispatch<SetStateAction<CompSelectionState>>,
  source: RecommendedCompsSources,
  orderID: string,
  isInspectifyOrder: boolean,
  hasArvOption: boolean,
  locationType: LocationType | undefined,
  proximityRequirementMiles: number | undefined,
  effectiveDate: AloftDate | undefined,
  saleDateRequirementMonths: number | undefined,
  compSelectionTableVariant: 'sales' | 'rental' = 'sales',
  imageAnalysisRatingsEnabled: boolean = false,
): {
  table: Table<CompSelectionProperty>;
  rowsToPin: number;
  tableData: CompSelectionProperty[];
} => {
  const tableData = useMemo(() => [subject, ...data], [subject, data]);

  const rowsToPin = 1;

  // Use a ref to store the current rowSelection so cells always read the latest value
  // without causing the column definition to be recreated
  const rowSelectionRef = useRef<CompSelectionState>(rowSelection);
  useEffect(() => {
    rowSelectionRef.current = rowSelection;
  }, [rowSelection]);

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<CompSelectionProperty>();

    const statusCol = columnHelper.accessor((p) => p.mlsStatus, {
      id: 'Status',
      cell: ({ table, row }) => {
        const sortedRows = table.getSortedRowModel().rows;
        const visibleIndex = sortedRows.findIndex((r) => r.id === row.id);
        return compSelectionTableVariant === 'rental'
          ? rentalStatusColumn(row.original, visibleIndex)
          : statusColumn(row.original, visibleIndex);
      },
    });

    const [qualityCol, conditionCol] = imageAnalysisRatingsEnabled
      ? [
          columnHelper.accessor((p) => p.uadQualityRating, {
            id: 'Qlty.',
            header: () => (
              <UADRatingHeader
                label="Qlty."
                tooltip="AI Image Analysis recommended UAD quality rating"
              />
            ),
            cell: ({ row: { original } }) => (
              <UADRatingCell
                rating={original.uadQualityRating}
                prefix="Q"
                isLoading={original.uadRatingsLoading}
                hasFailed={original.uadQualityRatingFailed}
                isSkipped={original.uadRatingsSkipped}
                failureTooltip="Failed to analyze the quality rating"
                commentary={original.uadQualityRatingCommentary}
              />
            ),
          }),
          columnHelper.accessor((p) => p.uadConditionRating, {
            id: 'Cond.',
            header: () => (
              <UADRatingHeader
                label="Cond."
                tooltip="AI Image Analysis recommended UAD condition rating"
              />
            ),
            cell: ({ row: { original } }) => (
              <UADRatingCell
                rating={original.uadConditionRating}
                prefix="C"
                isLoading={original.uadRatingsLoading}
                hasFailed={original.uadConditionRatingFailed}
                isSkipped={original.uadRatingsSkipped}
                failureTooltip="Failed to analyze the condition rating"
                commentary={original.uadConditionRatingCommentary}
              />
            ),
          }),
        ]
      : [undefined, undefined];

    if (compSelectionTableVariant === 'rental') {
      return [
        makeSelectionColumn(
          () => rowSelectionRef.current,
          setRowSelection,
          isInspectifyOrder,
          hasArvOption,
        ),
        statusCol,
        columnHelper.accessor((p) => formatAddressCell(p.address), {
          id: 'Address',
          cell: ({ row }) => addressColumn(row.original),
        }),
        columnHelper.accessor((p) => p.similarityScore, {
          id: 'Similarity Score',
          cell: ({ row: { original } }) =>
            isDefined(original.similarityScore)
              ? original.similarityScore.toFixed(2).toString()
              : '',
        }),
        columnHelper.accessor((p) => p.distanceToSubject, {
          id: 'Proximity',
          cell: ({ row: { original } }) => {
            if (original.id === 'subject' || original.distanceToSubject === undefined) return '';

            const distanceText = `${original.distanceToSubject} miles`;
            const showWarning =
              locationType !== undefined &&
              proximityRequirementMiles !== undefined &&
              original.distanceToSubject > proximityRequirementMiles;

            return (
              <Flex alignItems="center" justifyContent="space-between" width="100%" gap="8px">
                <Typography variant="body2" color="textPrimary">
                  {distanceText}
                </Typography>
                <Block width="18px" height="18px">
                  {showWarning && (
                    <Tooltip
                      label={formatProximityRequirementTooltip(
                        locationType!,
                        proximityRequirementMiles!,
                      )}
                    >
                      <Block
                        width="18px"
                        height="18px"
                        __dangerDoNotUseSx={{
                          backgroundColor: theme.colors.alertWarning,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon icon="warning" color="textOffset" size="sm" />
                      </Block>
                    </Tooltip>
                  )}
                </Block>
              </Flex>
            );
          },
        }),
        columnHelper.accessor((p) => p.leaseDate ?? p.saleDate, {
          id: 'Lease Date',
          cell: ({ row: { original } }) => formatDateCell(original.leaseDate ?? original.saleDate),
        }),
        columnHelper.accessor((p) => p.cumulativeDaysOnMarket, {
          id: 'CDOM',
          cell: ({ row: { original } }) =>
            original.cumulativeDaysOnMarket !== undefined
              ? `${original.cumulativeDaysOnMarket} days`
              : '',
        }),
        columnHelper.accessor(
          (p) => p.rentRate?.toNumericPrice() ?? p.listPrice?.toNumericPrice(),
          {
            id: 'Rent Rate',
            cell: ({ row: { original } }) =>
              (original.rentRate ?? original.listPrice)?.toString(true) ?? '',
          },
        ),
        columnHelper.accessor((p) => p.propertyType, {
          id: 'Property Type',
          cell: ({ row }) => row.original.propertyType,
        }),
        columnHelper.accessor((p) => p.yearBuilt, {
          id: 'Year Built',
          cell: ({ row: { original } }) => original.yearBuilt?.toString(),
        }),
        columnHelper.accessor((p) => p.gla?.toSqFt().getUnitAmount(), {
          id: 'GLA',
          cell: ({ row: { original } }) => original.gla?.toString(),
        }),
        columnHelper.accessor((p) => p.lotSize?.toSqFt().getUnitAmount(), {
          id: 'Lot Size',
          cell: ({ row: { original } }) => original.lotSize?.toRoundedString(),
        }),
        columnHelper.accessor((p) => p.rentRate ?? p.listPrice, {
          id: 'Rent / sqft',
          cell: ({ row: { original } }) => {
            const rent = original.rentRate ?? original.listPrice;
            const glaSqft = original.gla?.toSqFt().getUnitAmount();
            return rent && glaSqft ? `${rent.div(glaSqft || 1).toString(true)}/sqft` : '';
          },
        }),
        columnHelper.accessor((p) => p.bedrooms, {
          id: 'Beds',
          cell: ({ row: { original } }) => original.bedrooms?.toString(),
        }),
        columnHelper.accessor((p) => p.fullBaths, {
          id: 'Full Baths',
          cell: ({ row: { original } }) => original.fullBaths?.toString(),
        }),
        columnHelper.accessor((p) => p.halfBaths, {
          id: 'Half Baths',
          cell: ({ row: { original } }) => original.halfBaths?.toString(),
        }),
        columnHelper.accessor((p) => p.garage, {
          id: 'Garage',
          cell: ({ row: { original } }) => booleanToYesNo(original.garage),
        }),
        columnHelper.accessor((p) => p.garageCount, {
          id: 'Garage Count',
          cell: ({ row: { original } }) => original.garageCount?.toString(),
        }),
        columnHelper.accessor((p) => p.basement, {
          id: 'Basement',
          cell: ({ row: { original } }) => booleanToYesNo(original.basement),
        }),
        columnHelper.accessor((p) => p.pool, {
          id: 'Pool',
          cell: ({ row: { original } }) => booleanToYesNo(original.pool),
        }),
      ].filter(isDefined);
    }

    return [
      makeSelectionColumn(
        () => rowSelectionRef.current,
        setRowSelection,
        isInspectifyOrder,
        hasArvOption,
      ),
      statusCol,
      columnHelper.accessor((p) => formatAddressCell(p.address), {
        id: 'Address',
        cell: ({ row }) => addressColumn(row.original),
      }),
      columnHelper.accessor((p) => p.similarityScore, {
        id: 'Similarity Score',
        cell: ({ row: { original } }) =>
          isDefined(original.similarityScore) ? original.similarityScore.toFixed(2).toString() : '',
      }),
      columnHelper.accessor((p) => p.distanceToSubject, {
        id: 'Proximity',
        cell: ({ row: { original } }) => {
          if (original.id === 'subject' || original.distanceToSubject === undefined) return '';

          const distanceText = `${original.distanceToSubject} miles`;
          const showWarning =
            locationType !== undefined &&
            proximityRequirementMiles !== undefined &&
            original.distanceToSubject > proximityRequirementMiles;

          return (
            <Flex alignItems="center" justifyContent="space-between" width="100%" gap="8px">
              <Typography variant="body2" color="textPrimary">
                {distanceText}
              </Typography>
              <Block width="18px" height="18px">
                {showWarning && (
                  <Tooltip
                    label={formatProximityRequirementTooltip(
                      locationType!,
                      proximityRequirementMiles!,
                    )}
                  >
                    <Block
                      width="18px"
                      height="18px"
                      __dangerDoNotUseSx={{
                        backgroundColor: theme.colors.alertWarning,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon icon="warning" color="textOffset" size="sm" />
                    </Block>
                  </Tooltip>
                )}
              </Block>
            </Flex>
          );
        },
      }),
      qualityCol,
      conditionCol,
      columnHelper.accessor((p) => p.saleDate, {
        id: 'Sale Date',
        cell: ({ row: { original } }) => {
          if (original.id === 'subject') return formatDateCell(original.saleDate);

          const showWarning =
            locationType !== undefined &&
            isSaleDateOutOfBounds(original.saleDate, effectiveDate, saleDateRequirementMonths);

          return (
            <Flex alignItems="center" justifyContent="space-between" width="100%" gap="8px">
              <Typography variant="body2" color="textPrimary">
                {formatDateCell(original.saleDate)}
              </Typography>
              <Block width="18px" height="18px">
                {showWarning && saleDateRequirementMonths !== undefined && (
                  <Tooltip
                    label={formatSaleDateRequirementTooltip(
                      locationType!,
                      saleDateRequirementMonths,
                    )}
                  >
                    <Block
                      width="18px"
                      height="18px"
                      __dangerDoNotUseSx={{
                        backgroundColor: theme.colors.alertWarning,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon icon="warning" color="textOffset" size="sm" />
                    </Block>
                  </Tooltip>
                )}
              </Block>
            </Flex>
          );
        },
      }),
      columnHelper.accessor((p) => p.contractDate, {
        id: 'Contract Date',
        cell: ({ row: { original } }) => formatDateCell(original.contractDate),
      }),
      columnHelper.accessor((p) => p.cumulativeDaysOnMarket, {
        id: 'CDOM',
        cell: ({ row: { original } }) => original.cumulativeDaysOnMarket?.toString(),
      }),
      columnHelper.accessor((p) => p.salePrice?.toNumericPrice(), {
        id: 'Sale Price',
        cell: ({ row: { original } }) => original.salePrice?.toString(true),
      }),
      columnHelper.accessor((p) => p.listPrice?.toNumericPrice(), {
        id: 'List Price',
        cell: ({ row: { original } }) => original.listPrice?.toString(true),
      }),
      hasArvOption
        ? columnHelper.accessor((p) => p.qualityTier, {
            id: 'Quality Tier',
            cell: ({ row: { original } }) => {
              if (original.qualityTier === undefined) return '';
              return (
                <Flex alignItems="center" gap="8px">
                  <Block
                    width="16px"
                    height="16px"
                    backgroundColor={`qualityTier${original.qualityTier}` as ProductColor}
                  />
                  Tier {original.qualityTier}
                </Flex>
              );
            },
          })
        : undefined,
      columnHelper.accessor((p) => p.propertyType, {
        id: 'Property Type',
        cell: ({ row }) => row.original.propertyType,
      }),
      columnHelper.accessor((p) => p.yearBuilt, {
        id: 'Year Built',
        cell: ({ row: { original } }) => original.yearBuilt?.toString(),
      }),
      columnHelper.accessor((p) => p.gla?.toSqFt().getUnitAmount(), {
        id: 'GLA',
        cell: ({ row: { original } }) => original.gla?.toString(),
      }),
      columnHelper.accessor((p) => p.lotSize?.toSqFt().getUnitAmount(), {
        id: 'Lot Size',
        cell: ({ row: { original } }) => original.lotSize?.toRoundedString(),
      }),
      columnHelper.accessor((p) => p.salePrice, {
        id: 'Sqft Price',
        cell: ({ row: { original } }) => {
          const glaSqft = original.gla?.toSqFt().getUnitAmount();
          return original.salePrice && glaSqft
            ? `${original.salePrice.div(glaSqft || 1).toString(true)}/sqft`
            : '';
        },
      }),
      columnHelper.accessor((p) => p.bedrooms, {
        id: 'Beds',
        cell: ({ row: { original } }) => original.bedrooms?.toString(),
      }),
      columnHelper.accessor((p) => p.fullBaths, {
        id: 'Full Baths',
        cell: ({ row: { original } }) => original.fullBaths?.toString(),
      }),
      columnHelper.accessor((p) => p.halfBaths, {
        id: 'Half Baths',
        cell: ({ row: { original } }) => original.halfBaths?.toString(),
      }),
      columnHelper.accessor((p) => p.garage, {
        id: 'Garage',
        cell: ({ row: { original } }) => booleanToYesNo(original.garage),
      }),
      columnHelper.accessor((p) => p.garageCount, {
        id: 'Garage Count',
        cell: ({ row: { original } }) => original.garageCount?.toString(),
      }),
      columnHelper.accessor((p) => p.basement, {
        id: 'Basement',
        cell: ({ row: { original } }) => booleanToYesNo(original.basement),
      }),
      columnHelper.accessor((p) => p.pool, {
        id: 'Pool',
        cell: ({ row: { original } }) => booleanToYesNo(original.pool),
      }),
      columnHelper.accessor((p) => p.reo, {
        id: 'REO',
        cell: ({ row: { original } }) => booleanToYesNo(original.reo),
      }),
      columnHelper.accessor((p) => p.shortSale, {
        id: 'Short Sale',
        cell: ({ row: { original } }) => booleanToYesNo(original.shortSale),
      }),
    ].filter(isDefined);
  }, [
    compSelectionTableVariant,
    comps.length,
    hasArvOption,
    isInspectifyOrder,
    locationType,
    orderID,
    proximityRequirementMiles,
    effectiveDate,
    saleDateRequirementMonths,
    rowsToPin,
    setRowSelection,
    source,
  ]);

  const table = useReactTable({
    data: tableData,
    columns,
    enableSorting: true,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModelWithPinnedSubject(rowsToPin),
    getPaginationRowModel: getPaginationRowModel(),
    state: { sorting },
    initialState: {
      pagination: {
        pageIndex: INITIAL_PAGE_INDEX,
        pageSize: PAGE_SIZE,
      },
    },
  });

  return { table, rowsToPin, tableData };
};
